import React, { useId } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Button } from '@orijinworks/frontend-commons';
import {
  buildBulkProvisionTitle,
  formatSelectedLocationNames,
  getBulkRowCurrentRoleLabel,
  isBulkRowRoleChanged,
} from '../utils/canvas-access.utils';
import styles from './StaffCanvasAccessBulkBar.style';

const BulkProvisionPanelShell = ({
  classes,
  title,
  subtitle = null,
  titleId,
  cancelBulk,
  submitting,
  isBulkSaveDisabled,
  saveBulk,
  rolesLoading,
  children = null,
}) => (
  <Box className={classes.card} role="region" aria-labelledby={titleId} mb={2}>
    <Box className={classes.header}>
      <Box className={classes.headerText}>
        <Typography id={titleId} variant="subtitle1" className={classes.headerTitle}>
          {title}
        </Typography>
        {subtitle && (
          <Tooltip title={subtitle.full} placement="bottom-start">
            <Typography variant="body2" className={classes.headerSubtitle} noWrap>
              {subtitle.display}
            </Typography>
          </Tooltip>
        )}
      </Box>
      <IconButton
        aria-label="Close"
        onClick={cancelBulk}
        disabled={submitting}
        className={classes.closeButton}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box className={classes.body}>
      {rolesLoading ? (
        <Box className={classes.bodyLoading}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        children
      )}
    </Box>
    <Box className={classes.footer}>
      <Button size="small" variant="outlined" onClick={cancelBulk} disabled={submitting}>
        Cancel
      </Button>
      <Button
        size="small"
        color="primary"
        variant="contained"
        onClick={saveBulk}
        disabled={submitting || isBulkSaveDisabled}
      >
        {submitting && (
          <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
        )}
        Save
      </Button>
    </Box>
  </Box>
);

BulkProvisionPanelShell.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.shape({
    display: PropTypes.string.isRequired,
    full: PropTypes.string.isRequired,
  }),
  titleId: PropTypes.string.isRequired,
  cancelBulk: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  isBulkSaveDisabled: PropTypes.bool.isRequired,
  saveBulk: PropTypes.func.isRequired,
  rolesLoading: PropTypes.bool.isRequired,
  children: PropTypes.node,
};

/**
 * Display-only bulk selection and multi-location Canvas role provisioning controls.
 */
const StaffCanvasAccessBulkBar = ({
  classes,
  visible = true,
  submitting,
  roleOptionsByLocation,
  bulkSameRoleDraftId = '',
  cancelBulk,
  clearSelection,
  commonRoleIntersection,
  draftRoleByLocation,
  handleBulkDifferentRoleChange,
  handleBulkSameRoleChange,
  isBulkSaveDisabled,
  rolesLoading,
  saveBulk,
  selectedLocationKeys,
  selectedRows,
  showDifferentRolesPanel,
  showSameRolePanel,
  showSelectionBar,
  startBulkDifferentRoles,
  startBulkSameRole,
}) => {
  const sameRoleTitleId = useId();
  const differentRolesTitleId = useId();
  const sameRoleSelectId = useId();

  if (!visible) {
    return null;
  }

  if (showSelectionBar) {
    return (
      <Box className={classes.bulkBar} mb={2}>
        <Box className={classes.selectionLeft}>
          <Typography variant="body2" className={classes.selectionSummary}>
            {selectedLocationKeys.length} selected
          </Typography>
          <Typography
            component="button"
            type="button"
            variant="body2"
            className={classes.clearSelection}
            onClick={clearSelection}
          >
            Clear
          </Typography>
        </Box>
        <Box className={classes.selectionActions}>
          <Button size="small" color="primary" variant="contained" onClick={startBulkSameRole}>
            Provision with same role
          </Button>
          <Button size="small" color="primary" variant="outlined" onClick={startBulkDifferentRoles}>
            Provision with different roles
          </Button>
        </Box>
      </Box>
    );
  }

  if (showSameRolePanel) {
    const locationNames = formatSelectedLocationNames(selectedRows);

    return (
      <BulkProvisionPanelShell
        classes={classes}
        title={buildBulkProvisionTitle(selectedRows.length, 'same')}
        subtitle={locationNames}
        titleId={sameRoleTitleId}
        cancelBulk={cancelBulk}
        submitting={submitting}
        isBulkSaveDisabled={isBulkSaveDisabled}
        saveBulk={saveBulk}
        rolesLoading={rolesLoading}
      >
        <Box className={classes.sameRoleField}>
          <Typography
            component="label"
            htmlFor={sameRoleSelectId}
            variant="body2"
            className={classes.sameRoleLabel}
          >
            Apply role to all:
          </Typography>
          <Select
            id={sameRoleSelectId}
            variant="outlined"
            className={classes.sameRoleSelect}
            displayEmpty
            disabled={commonRoleIntersection.length === 0}
            value={bulkSameRoleDraftId}
            onChange={handleBulkSameRoleChange}
          >
            <MenuItem value="" disabled>
              Select common role
            </MenuItem>
            {commonRoleIntersection.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {commonRoleIntersection.length === 0 && (
          <Typography variant="body2" color="textSecondary" className={classes.hint}>
            No common Canvas roles across selected locations. Use “Provision with different roles” instead.
          </Typography>
        )}
      </BulkProvisionPanelShell>
    );
  }

  if (showDifferentRolesPanel) {
    return (
      <BulkProvisionPanelShell
        classes={classes}
        title={buildBulkProvisionTitle(selectedRows.length, 'individual')}
        titleId={differentRolesTitleId}
        cancelBulk={cancelBulk}
        submitting={submitting}
        isBulkSaveDisabled={isBulkSaveDisabled}
        saveBulk={saveBulk}
        rolesLoading={rolesLoading}
      >
        <Box className={classes.differentRoleList}>
          {selectedRows.map((row) => {
            const roles = roleOptionsByLocation[row.locationKey] ?? [];
            const draftRoleId = draftRoleByLocation[row.locationKey] ?? '';
            const currentRoleLabel = getBulkRowCurrentRoleLabel(row);
            const roleChanged = isBulkRowRoleChanged(row, draftRoleId);

            const rowClassName = roleChanged
              ? `${classes.differentRoleRow} ${classes.differentRoleRowChanged}`
              : classes.differentRoleRow;

            return (
              <Box
                key={row.locationKey}
                className={rowClassName}
              >
                <Box className={classes.facilityInfo}>
                  <Typography variant="body2" className={classes.facilityName} noWrap title={row.locationName || String(row.locationKey)}>
                    {row.locationName || row.locationKey}
                  </Typography>
                </Box>
                <Box className={classes.roleFieldGroup}>
                  <Select
                    variant="outlined"
                    className={classes.roleSelect}
                    displayEmpty
                    value={draftRoleId}
                    onChange={(event) => handleBulkDifferentRoleChange(row.locationKey, event.target.value)}
                  >
                    <MenuItem value="" disabled>
                      Select role
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="body2" className={classes.currentRole}>
                    {currentRoleLabel ? `Current: ${currentRoleLabel}` : 'Current: —'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </BulkProvisionPanelShell>
    );
  }

  return null;
};

StaffCanvasAccessBulkBar.propTypes = {
  visible: PropTypes.bool,
  submitting: PropTypes.bool.isRequired,
  roleOptionsByLocation: PropTypes.object.isRequired,
  bulkSameRoleDraftId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  cancelBulk: PropTypes.func.isRequired,
  clearSelection: PropTypes.func.isRequired,
  commonRoleIntersection: PropTypes.arrayOf(PropTypes.object).isRequired,
  draftRoleByLocation: PropTypes.object.isRequired,
  handleBulkDifferentRoleChange: PropTypes.func.isRequired,
  handleBulkSameRoleChange: PropTypes.func.isRequired,
  isBulkSaveDisabled: PropTypes.bool.isRequired,
  rolesLoading: PropTypes.bool.isRequired,
  saveBulk: PropTypes.func.isRequired,
  selectedLocationKeys: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  showDifferentRolesPanel: PropTypes.bool.isRequired,
  showSameRolePanel: PropTypes.bool.isRequired,
  showSelectionBar: PropTypes.bool.isRequired,
  startBulkDifferentRoles: PropTypes.func.isRequired,
  startBulkSameRole: PropTypes.func.isRequired,
};

export default withStyles(styles)(StaffCanvasAccessBulkBar);
